import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, DatePicker, Empty, Input, Modal, Popconfirm, Popover, Segmented, Table, Tooltip, Typography, message,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import BarraLista, { BotonActualizar } from './BarraLista';
import { FctAviso, FctEstado, FctIcono, fmtFecha, fmtFechaHora } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';
import { usePermissions } from '../../contexts/PermissionContext';
import {
  BandejaListado, ElementoBandeja, PeticionAprobar, TipoBandeja, VistaBandeja, aprobarBandeja, descartarBandeja,
  getBandeja,
} from '../../api/cxPanelApi';
import { BuscadorCliente, TipoFiltro } from './CampanaSemanaTab';
import {
  FILTRO_TODOS, FiltroTipo, MOTIVO_MAX, MOTIVO_MIN, RECURSO_BANDEJA_WRITE, TIPOS_BANDEJA, TIPO_BANDEJA_DESCRIPCION, TIPO_BANDEJA_NOMBRE,
  aIsoDia, aprobadoPorLegible, cicloLegible, coincideBusqueda, coincideTipo, contarAbiertas, contarTipos,
  equipoLegible, esAbierta, esSeleccionable, estadoBandeja, mensajeAprobacion, mensajeDescarte, mensajeError,
  mensajeErrorAccion, motivoLegible, motivoValido, pendientesDeTipo, plural, presetsRango, textoConfirmarAprobar,
} from './panelCx';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/** Rango máximo del histórico que admite el backend (días entre «desde» y «hasta»). */
const MAX_DIAS_RANGO = 366;

/** Contadores del filtro de tipo en una sola tinta: el tipo ya lo dice su código, el color no añade nada. */
const COLORES_TIPO: Record<string, string> = {
  V0: fct.marca['verde-oscuro'],
  V1: fct.marca['verde-oscuro'],
  V2: fct.marca['verde-oscuro'],
  F2: fct.marca['verde-oscuro'],
  F4O: fct.marca['verde-oscuro'],
};

/** Etiqueta del tipo con su nombre (no el código V0, V1…), en una tinta, con su explicación en el tooltip. */
const TipoEnvioTag: React.FC<{ tipo: string }> = ({ tipo }) => (
  <Tooltip title={TIPO_BANDEJA_DESCRIPCION[tipo]}>
    <span
      style={{
        display: 'inline-block', padding: '0 6px', borderRadius: 4, fontSize: 12, fontWeight: 600, lineHeight: '20px',
        color: fct.tinta['900'], background: fct.superficie.hundida, border: `1px solid ${fct.linea.control}`,
      }}
    >
      {TIPO_BANDEJA_NOMBRE[tipo] ?? tipo}
    </span>
  </Tooltip>
);

/**
 * Cuándo se prepara cada correo y cuándo sale, con los ciclos de la F2, para consultarlo sin salir de la
 * pantalla (mismo patrón que «Reglas de envío» de la campaña). Fuente: 014_pr_bandeja_preparar.sql y el flujo
 * n8n cx-envios-diarios (lunes a viernes, 09:00).
 */
const ReglasBandeja: React.FC = () => {
  const celda: React.CSSProperties = { padding: '9px 10px', borderTop: `1px solid ${fct.linea.fila}`, verticalAlign: 'top' };
  const cabecera: React.CSSProperties = {
    padding: '6px 10px', textAlign: 'left', fontFamily: fct.fuente.dato, fontSize: 11, fontWeight: 500,
    letterSpacing: '.08em', textTransform: 'uppercase', color: fct.tinta['500'],
  };
  const solo = (si: boolean) => (si
    ? <FctEstado personalizado={{ etiqueta: 'Sí, sale solo', familia: 'exito' }} />
    : <FctEstado personalizado={{ etiqueta: 'No: lo aprueba CxCp', familia: 'aviso' }} />);
  const filas: { tipo: string; cuando: React.ReactNode; necesita: string; automatico: boolean }[] = [
    { tipo: 'V2', cuando: 'Cuando la operación entra en Fase 1. Al vendedor.', necesita: 'Email, asesor, analista y jurídico', automatico: true },
    {
      tipo: 'F2',
      cuando: (
        <>
          A los días 15, 45, 90, 135… del pase a Fase 2 (cada 45 días). Si la operación sale de Fase 2, deja de enviarse.
        </>
      ),
      necesita: 'Email',
      automatico: true,
    },
    { tipo: 'F4O', cuando: 'Cuando Mystery hace 6 llamadas sin contactar.', necesita: 'Email', automatico: false },
  ];
  return (
    <div style={{ width: 720, maxWidth: '85vw', display: 'grid', gap: 10 }}>
      <div
        style={{
          display: 'grid', gap: 2, padding: '8px 12px', borderRadius: 6,
          background: fct.superficie.hundida, border: `1px solid ${fct.linea.tarjeta}`, color: fct.tinta['900'],
        }}
      >
        <span><Text strong>Cada día laborable a las 09:00</Text> se preparan los correos y se envían.</span>
        <span>Los automáticos salen solos. La F4 online solo sale si CxCp la aprueba antes.</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
        <thead>
          <tr>
            <th style={{ ...cabecera, width: 170 }}>Correo</th>
            <th style={cabecera}>Cuándo se manda</th>
            <th style={{ ...cabecera, width: 140 }}>Necesita</th>
            <th style={{ ...cabecera, width: 150 }}>¿Sale solo?</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.tipo}>
              <td style={celda}><TipoEnvioTag tipo={f.tipo} /></td>
              <td style={{ ...celda, color: fct.tinta['900'] }}>{f.cuando}</td>
              <td style={{ ...celda, color: fct.tinta['700'] }}>{f.necesita}</td>
              <td style={celda}>{solo(f.automatico)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'grid', gap: 2, fontSize: 12.5, color: fct.tinta['700'] }}>
        <span>• Si falta algo de «Necesita», el correo queda <Text strong>Bloqueado</Text> y no sale hasta que se complete en Gescom.</span>
        <span>• Solo se tienen en cuenta los casos de los últimos 30 días.</span>
        <span>• Un correo descartado no vuelve a salir.</span>
      </div>
    </div>
  );
};

/**
 * Estado con el semáforo del manual (icono + etiqueta) y, a la vista, el porqué: en bloqueado, qué
 * falta (con el equipo en el tooltip para saber a quién avisar); en descartado, el motivo.
 */
const EstadoCelda: React.FC<{ fila: ElementoBandeja }> = ({ fila }) => {
  const e = estadoBandeja(fila);
  const motivo = motivoLegible(fila.motivo);
  const estado = <FctEstado personalizado={e} />;

  if (fila.estado === 'APROBADO') {
    const quien = aprobadoPorLegible(fila);
    return quien ? <Tooltip title={quien}><span>{estado}</span></Tooltip> : estado;
  }

  if ((fila.estado === 'BLOQUEADO' || fila.estado === 'DESCARTADO') && motivo) {
    const detalle = fila.estado === 'BLOQUEADO'
      ? equipoLegible(fila.equipo)
      : (fila.descartadoEl ? `Descartado el ${fmtFechaHora(fila.descartadoEl)}` : null);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px' }}>
        {estado}
        <Tooltip title={detalle}>
          <span style={{ color: fila.estado === 'BLOQUEADO' ? fct.tinta['900'] : fct.tinta['700'] }}>{motivo}</span>
        </Tooltip>
      </div>
    );
  }

  return estado;
};

interface Props {
  /** Título y selector de vista: van al principio de la barra (una sola fila con los filtros). */
  cabecera: React.ReactNode;
  /** Texto del buscador (común a las vistas): nombre, código CL, email, CCL u operación. */
  busqueda: string;
  onBusqueda: (v: string) => void;
  /** Se llama cada vez que se cargan las abiertas (al entrar, al actualizar y tras aprobar o descartar). */
  onAbiertas?: (n: number) => void;
}

/**
 * «Pendientes de envío»: los correos por evento (V2, F2 y F4 online) que el sistema prepara
 * solo cada día. CxCp los aprueba (salen en el próximo envío) o los descarta con un motivo. Lo
 * bloqueado (falta el email del cliente o alguien del equipo) no se aprueba: se desbloquea solo
 * cuando se completa. «Cerradas» es el histórico de lo enviado y lo descartado.
 * Sin el permiso `Cx.Bandeja.Write` la vista es de solo lectura.
 */
const BandejaTab: React.FC<Props> = ({ cabecera, busqueda, onBusqueda, onAbiertas }) => {
  const { hasPermission, isLoading: cargandoPermisos } = usePermissions();
  const puedeEscribir = hasPermission(RECURSO_BANDEJA_WRITE);

  const [vista, setVistaEstado] = useState<VistaBandeja>('abiertas');
  const [rango, setRango] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(30, 'day').startOf('day'), dayjs().startOf('day')]);
  const [tipo, setTipo] = useState<FiltroTipo>(FILTRO_TODOS);
  const [listado, setListado] = useState<BandejaListado | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<number[]>([]);
  /** Acción en curso (bloquea los botones mientras tanto). */
  const [trabajando, setTrabajando] = useState<'aprobar' | 'aprobarTodos' | 'descartar' | null>(null);
  /** Envíos en la ventana de descarte (null = cerrada). */
  const [aDescartar, setADescartar] = useState<ElementoBandeja[] | null>(null);
  const [motivo, setMotivo] = useState('');
  /** Descarta respuestas de peticiones viejas si se cambia de vista o de fechas rápido. */
  const peticion = useRef(0);
  /** El aviso a la página no debe relanzar la carga si la página cambia de función. */
  const avisarAbiertas = useRef(onAbiertas);
  avisarAbiertas.current = onAbiertas;

  const setVista = (v: VistaBandeja) => {
    setVistaEstado(v);
    setListado(null);
    setSeleccion([]);
  };

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setLoading(true);
    setError(null);
    try {
      const datos = await getBandeja(vista === 'abiertas'
        ? { estado: 'abiertas' }
        : { estado: 'cerradas', desde: aIsoDia(rango[0]), hasta: aIsoDia(rango[1]) });
      if (id !== peticion.current) return;
      setListado(datos);
      // Lo marcado que ya no está pendiente (lo aprobó otra persona, se bloqueó…) deja de estar marcado.
      setSeleccion((s) => s.filter((x) => datos.items.some((i) => i.id === x && esSeleccionable(i))));
      if (vista === 'abiertas') avisarAbiertas.current?.(contarAbiertas(datos.items));
    } catch (err) {
      if (id !== peticion.current) return;
      setListado(null);
      setError(mensajeError(err, vista === 'abiertas' ? 'los envíos pendientes' : 'el histórico de envíos'));
    } finally {
      if (id === peticion.current) setLoading(false);
    }
  }, [vista, rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const items = useMemo(() => listado?.items ?? [], [listado]);
  const modoPrueba = listado?.modoPrueba === true;
  const buzonesPrueba = listado?.destinatariosPrueba ?? [];
  /** Buzón de prueba al que saldrá de verdad (la API usa la misma regla: id % número de buzones). */
  const buzonDe = (id: number): string | null =>
    (modoPrueba && buzonesPrueba.length > 0 ? buzonesPrueba[Math.abs(id) % buzonesPrueba.length] : null);

  // Contadores sobre lo que pasa el buscador; la tabla, además, por el tipo elegido.
  const buscadas = useMemo(
    () => items.filter((f) => coincideBusqueda(busqueda, f.nombre, f.codigoCliente, f.email, f.ccl, f.nombreOperacion, buzonDe(f.id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, busqueda, modoPrueba, buzonesPrueba],
  );
  const conteos = useMemo(() => contarTipos(buscadas), [buscadas]);
  const visibles = useMemo(() => buscadas.filter((f) => coincideTipo(tipo, f.tipo)), [buscadas, tipo]);
  const filtrando = visibles.length !== items.length;

  // Solo cuenta lo marcado que se ve: si un filtro esconde una fila marcada, no se aprueba a ciegas.
  const seleccionVisible = useMemo(() => {
    const aprobables = new Set(visibles.filter(esSeleccionable).map((f) => f.id));
    return seleccion.filter((id) => aprobables.has(id));
  }, [seleccion, visibles]);
  const marcadas = seleccionVisible.length;

  const tipoElegido = tipo !== FILTRO_TODOS && (TIPOS_BANDEJA as string[]).includes(tipo) ? (tipo as TipoBandeja) : null;
  // «Aprobar todos los de tipo X» aprueba en el backend TODOS los pendientes de ese tipo, también los que no
  // pasan la búsqueda: el contador es el de todos.
  const pendientesTipo = tipoElegido ? pendientesDeTipo(items, tipoElegido) : 0;

  const aprobar = async (peticionAprobar: PeticionAprobar) => {
    setTrabajando('ids' in peticionAprobar ? 'aprobar' : 'aprobarTodos');
    try {
      const resultado = await aprobarBandeja(peticionAprobar);
      const m = mensajeAprobacion(resultado);
      message[m.tipo](m.texto, m.tipo === 'success' ? 4 : 8);
      setSeleccion([]);
    } catch (err) {
      message.error(mensajeErrorAccion(err, 'aprobar'), 8);
    } finally {
      setTrabajando(null);
      cargar();
    }
  };

  const abrirDescarte = (filas: ElementoBandeja[]) => {
    setMotivo('');
    setADescartar(filas);
  };

  const descartar = async () => {
    if (!aDescartar || !motivoValido(motivo)) return;
    const ids = aDescartar.map((f) => f.id);
    setTrabajando('descartar');
    try {
      const { descartados } = await descartarBandeja(ids, motivo.trim());
      const m = mensajeDescarte(ids.length, descartados);
      message[m.tipo](m.texto, m.tipo === 'success' ? 4 : 8);
      setADescartar(null);
      setSeleccion((s) => s.filter((x) => !ids.includes(x)));
    } catch (err) {
      message.error(mensajeErrorAccion(err, 'descartar'), 8);
      // Un 409 o un 403 no se arreglan reintentando con lo mismo: se cierra la ventana y se ve la lista al día.
      setADescartar(null);
    } finally {
      setTrabajando(null);
      cargar();
    }
  };

  const puedeAccionar = vista === 'abiertas' && puedeEscribir;

  const columnas: ColumnsType<ElementoBandeja> = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 175,
      render: (v: string) => <TipoEnvioTag tipo={v} />,
    },
    {
      title: 'CL',
      dataIndex: 'codigoCliente',
      width: 105,
      render: (v: string | null | undefined, f: ElementoBandeja) => (
        <span className="fct-mono">{v || <Text type="secondary">#{f.idCliente}</Text>}</span>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'nombre',
      ellipsis: true,
      render: (v?: string | null) => v || <Text type="secondary">Sin nombre</Text>,
    },
    {
      title: 'CCL',
      dataIndex: 'ccl',
      width: 150,
      render: (v?: string | null) => (v ? <span className="fct-mono">{v}</span> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Operación',
      dataIndex: 'nombreOperacion',
      width: 170,
      ellipsis: true,
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Destinatario',
      dataIndex: 'email',
      width: 230,
      ellipsis: true,
      // En modo prueba el email del cliente se enseña tachado y en gris: NO es a quien llega el correo.
      render: (v: string | null | undefined, f: ElementoBandeja) => {
        const buzon = buzonDe(f.id);
        if (!buzon) return v || <Text type="secondary">Sin email en Gescom</Text>;
        // Modo prueba: arriba, el buzón de prueba que lo recibirá de verdad; debajo, tachado, el email del cliente.
        return (
          <Tooltip title={`En modo prueba este correo llega a ${buzon}. El email tachado es el del cliente: en producción iría a él.`}>
            <div style={{ display: 'grid', lineHeight: 1.3, minWidth: 0 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span
                  style={{
                    flex: 'none', fontSize: 11, fontWeight: 600, padding: '0 6px', borderRadius: 999, lineHeight: '18px',
                    background: fct.estado.aviso.relleno, color: fct.estado.aviso.texto,
                  }}
                >
                  prueba
                </span>
                <span className="fct-mono" style={{ fontSize: 12.5, color: fct.tinta['900'], overflow: 'hidden', textOverflow: 'ellipsis' }}>{buzon}</span>
              </span>
              <span style={{ fontSize: 12, textDecoration: 'line-through', color: fct.tinta['500'], overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {v || 'sin email en Gescom'}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Solo en la F2: días desde el pase a Fase 2">Ciclo</Tooltip>,
      key: 'ciclo',
      width: 80,
      align: 'right',
      render: (_: unknown, f) => {
        const ciclo = cicloLegible(f);
        return ciclo ? <span className="fct-mono">{ciclo}</span> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 290,
      render: (_: unknown, f) => <EstadoCelda fila={f} />,
    },
    {
      title: 'Preparado',
      dataIndex: 'creadoEl',
      width: 105,
      render: (v?: string | null) => (v ? <span className="fct-mono">{fmtFecha(v)}</span> : <Text type="secondary">—</Text>),
      sorter: (a, b) => (a.creadoEl ?? '').localeCompare(b.creadoEl ?? ''),
    },
  ];

  // Descartar una sola fila: también lo bloqueado y lo aprobado que aún no ha salido (no se pueden marcar).
  if (puedeAccionar) {
    columnas.push({
      title: 'Acción',
      key: 'accion',
      width: 95,
      align: 'center',
      render: (_: unknown, f) => (esAbierta(f) ? (
        <Button size="small" type="link" disabled={!!trabajando} onClick={() => abrirDescarte([f])}>
          Descartar
        </Button>
      ) : null),
    });
  }

  const unaSola = aDescartar?.length === 1 ? aDescartar[0] : null;

  return (
    <>
      <BarraLista
        cabecera={cabecera}
        acciones={
          <>
            <Segmented<VistaBandeja>
              value={vista}
              onChange={setVista}
              options={[
                {
                  label: vista === 'abiertas' && !loading
                    ? <span>Por enviar <span className="fct-mono" style={{ color: fct.tinta['500'] }}>· {items.length}</span></span>
                    : 'Por enviar',
                  value: 'abiertas',
                  title: 'Por aprobar, bloqueados y aprobados que aún no han salido',
                },
                { label: 'Enviados y descartados', value: 'cerradas', title: 'Lo que ya salió o se descartó, por fechas' },
              ]}
            />
            {/* Cuándo sale y con qué reglas, en un solo botón (clic: las reglas), como en «Campaña». */}
            <Popover title="Reglas de envío" content={<ReglasBandeja />} trigger="click" placement="bottomRight">
              <Tooltip title="Ver cuándo se prepara y sale cada correo">
                <Button
                  icon={<InfoCircleOutlined />}
                  style={{
                    background: fct.estado.aviso.relleno,
                    borderColor: `${fct.estado.aviso.linea}55`,
                    color: fct.estado.aviso.texto,
                  }}
                >
                  Sale a las 09:00
                </Button>
              </Tooltip>
            </Popover>
          </>
        }
        filtros={
          <>
            <TipoFiltro
              tipos={TIPOS_BANDEJA}
              value={tipo}
              onChange={setTipo}
              conteos={conteos}
              total={buscadas.length}
              descripciones={TIPO_BANDEJA_DESCRIPCION}
              colores={COLORES_TIPO}
              nombres={TIPO_BANDEJA_NOMBRE}
              ancho={230}
            />
            {vista === 'cerradas' && (
              <RangePicker
                format="DD/MM/YYYY"
                allowClear={false}
                value={rango}
                onChange={(v) => {
                  if (v?.[0] && v?.[1]) setRango([v[0].startOf('day'), v[1].startOf('day')]);
                }}
                // Nada en el futuro y como mucho un año entre las dos fechas (límite del backend).
                disabledDate={(d, info) => d.isAfter(dayjs(), 'day')
                  || (!!info?.from && Math.abs(d.startOf('day').diff(info.from.startOf('day'), 'day')) > MAX_DIAS_RANGO)}
                presets={presetsRango()}
                style={{ width: 230 }}
              />
            )}
          </>
        }
      >
        <BuscadorCliente value={busqueda} onChange={onBusqueda} placeholder="Buscar: nombre, CL, email, CCL u operación" />
        <BotonActualizar onClick={cargar} loading={loading} />
      </BarraLista>

      {modoPrueba && (
        <div style={{ marginBottom: 12 }}>
          {/* Una sola línea: los buzones, al pasar el ratón (antes eran dos líneas de chips que empujaban la tabla). */}
          <FctAviso tipo="aviso" titulo="Modo prueba:">
            los correos aprobados no llegan al cliente: salen a{' '}
            <Tooltip
              title={(
                <div className="fct-mono" style={{ fontSize: 12 }}>
                  {(buzonesPrueba.length > 0 ? buzonesPrueba : ['las direcciones de prueba']).map((b) => <div key={b}>{b}</div>)}
                </div>
              )}
            >
              <Text strong underline style={{ cursor: 'help', color: 'inherit' }}>
                {plural(buzonesPrueba.length || 1, 'buzón de prueba', 'buzones de prueba')}
              </Text>
            </Tooltip>
            . Cada correo indica el suyo en «Destinatario»; el tachado es el del cliente.
          </FctAviso>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error}
          action={<Button size="small" onClick={cargar}>Reintentar</Button>}
        />
      )}

      {/* Acciones sobre la tabla (solo en «Abiertas»). Un único primario lima en la pantalla: aprobar lo marcado. */}
      {vista === 'abiertas' && puedeEscribir && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Popconfirm
            placement="bottomLeft"
            overlayStyle={{ maxWidth: 380 }}
            title={`Aprobar ${plural(marcadas, 'envío', 'envíos')}`}
            description={textoConfirmarAprobar(marcadas, modoPrueba)}
            okText="Aprobar"
            cancelText="Cancelar"
            disabled={marcadas === 0 || !!trabajando}
            onConfirm={() => aprobar({ ids: seleccionVisible })}
          >
            <Button
              type="primary"
              icon={<FctIcono nombre="check" style={{ width: 16, height: 16 }} />}
              disabled={marcadas === 0}
              loading={trabajando === 'aprobar'}
            >
              Aprobar seleccionados{marcadas > 0 ? ` (${marcadas})` : ''}
            </Button>
          </Popconfirm>
          <Button
            disabled={marcadas === 0 || !!trabajando}
            onClick={() => abrirDescarte(items.filter((f) => seleccionVisible.includes(f.id)))}
          >
            Descartar{marcadas > 0 ? ` (${marcadas})` : ''}
          </Button>
          {tipoElegido && (
            <Popconfirm
              placement="bottomLeft"
              overlayStyle={{ maxWidth: 380 }}
              title={`Aprobar todos los ${tipoElegido} pendientes`}
              description={`${textoConfirmarAprobar(pendientesTipo, modoPrueba)}${busqueda.trim()
                ? ' Incluye los que no salen en la búsqueda.' : ''}`}
              okText="Aprobar todos"
              cancelText="Cancelar"
              disabled={pendientesTipo === 0 || !!trabajando}
              onConfirm={() => aprobar({ todos: true, tipo: tipoElegido })}
            >
              <Button disabled={pendientesTipo === 0 || (!!trabajando && trabajando !== 'aprobarTodos')} loading={trabajando === 'aprobarTodos'}>
                Aprobar todos los {tipoElegido} ({pendientesTipo})
              </Button>
            </Popconfirm>
          )}
          <Text type="secondary" style={{ marginInlineStart: 4 }}>
            {marcadas > 0
              ? `${plural(marcadas, 'correo marcado', 'correos marcados')}.`
              : 'Casi todo sale solo a las 09:00. Aquí solo hay que aprobar la F4 online, y puedes descartar cualquier correo antes de que salga.'}
            {' '}Los «Bloqueado» no salen porque les falta un dato en Gescom (el email del cliente o alguien del equipo
            de la operación); cuando se completa, salen solos.
          </Text>
        </div>
      )}
      {vista === 'abiertas' && !puedeEscribir && !cargandoPermisos && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">Solo lectura: no tienes permiso para aprobar ni descartar envíos.</Text>
        </div>
      )}

      <Table<ElementoBandeja>
        rowKey="id"
        columns={columnas}
        dataSource={visibles}
        loading={loading}
        size="small"
// Con la tabla vacía, sin barra de desplazamiento horizontal (se veía una barra suelta bajo «No hay…»).
        scroll={visibles.length ? { x: 1400 } : undefined}
        rowSelection={puedeAccionar ? {
          selectedRowKeys: seleccionVisible,
          onChange: (keys) => setSeleccion(keys.map(Number)),
          // Solo lo pendiente se marca: lo bloqueado no se puede aprobar.
          getCheckboxProps: (f) => ({ disabled: !esSeleccionable(f), 'aria-label': `Marcar el envío ${f.tipo} de ${f.codigoCliente ?? f.idCliente}` }),
        } : undefined}
        locale={{
          emptyText: error
            ? <Empty description="No se han podido cargar los envíos" />
            : filtrando
              ? <Empty description="Ningún envío coincide con la búsqueda o el tipo" />
              : vista === 'abiertas'
                ? <Empty description="No hay envíos pendientes: todo está al día" />
                : <Empty description="No hay envíos enviados ni descartados en estas fechas" />,
        }}
        pagination={{
          defaultPageSize: 50,
          pageSizeOptions: [25, 50, 100, 200],
          showSizeChanger: true,
          showTotal: (t) => (filtrando ? `${t} de ${items.length} envíos` : `${t} envíos`),
        }}
      />

      <Modal
        open={aDescartar !== null}
        title={aDescartar ? `Descartar ${plural(aDescartar.length, 'envío', 'envíos')}` : ''}
        okText="Descartar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true, disabled: !motivoValido(motivo) }}
        confirmLoading={trabajando === 'descartar'}
        onOk={descartar}
        onCancel={() => setADescartar(null)}
        maskClosable={!trabajando}
      >
        {unaSola && (
          <Paragraph style={{ marginBottom: 8 }}>
            <TipoEnvioTag tipo={unaSola.tipo} />{' '}
            <span className="fct-mono">{unaSola.codigoCliente ?? `#${unaSola.idCliente}`}</span>
            {unaSola.nombre ? ` · ${unaSola.nombre}` : ''}
            {unaSola.nombreOperacion ? ` · ${unaSola.nombreOperacion}` : ''}
          </Paragraph>
        )}
        <Paragraph>
          Lo descartado no se vuelve a preparar: {aDescartar && aDescartar.length > 1
            ? 'estos clientes no recibirán estos correos.'
            : 'este cliente no recibirá este correo.'}
        </Paragraph>
        <label htmlFor="cx-motivo-descarte" style={{ display: 'block', marginBottom: 4, color: fct.tinta['900'], fontWeight: 500 }}>
          Motivo (obligatorio)
        </label>
        <Input.TextArea
          id="cx-motivo-descarte"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          maxLength={MOTIVO_MAX}
          showCount
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder="Por ejemplo: el analista ya se lo envió a mano"
        />
        {motivo.trim().length > 0 && motivo.trim().length < MOTIVO_MIN && (
          <Text type="secondary">Escribe al menos {MOTIVO_MIN} caracteres.</Text>
        )}
      </Modal>
    </>
  );
};

export default BandejaTab;
